import { ExperienceBookingContext } from 'contexts';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  EXPERIENCES_CATALOG_SCROLL_KEY,
  PER_PAGE_FOR_CATALOG,
} from 'constants';
import { useActiveFolder } from 'store';
import { pushEvent } from '@koob/google-tag-manager';
import { GTM_EVENTS } from '@koob/google-tag-manager/src/events';
import { assistantSessionFor } from 'components/Assistant/assistantSessions';

const ExperienceBookingProvider = ({ children }) => {
  const [rawExperienceFilters, setExperienceFilters] = useState({});
  // The global ClientFolder store is the single source of truth for the active
  // folder (US-015). The experience filters expose it via `clientFolder`, but
  // still allow transient per-booking overrides (edit/replace, folder-detail
  // booking start). An `undefined` clientFolder means "follow the store"; any
  // other value (a folder or null) is a local override that wins.
  const storeActiveFolder = useActiveFolder();
  const experienceFilters = useMemo(
    () => ({
      ...rawExperienceFilters,
      clientFolder:
        rawExperienceFilters.clientFolder === undefined
          ? (storeActiveFolder ?? undefined)
          : rawExperienceFilters.clientFolder,
    }),
    [rawExperienceFilters, storeActiveFolder],
  );
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogPerPage, setCatalogPerPage] = useState(PER_PAGE_FOR_CATALOG);

  const resetContext = useCallback(() => {
    setExperienceFilters({});
    setCatalogPage(1);
    setCatalogPerPage(PER_PAGE_FOR_CATALOG);
    sessionStorage.removeItem(EXPERIENCES_CATALOG_SCROLL_KEY);
  }, []);
  const [basket, setBasket] = useState([]);
  const addExperienceToBasket = useCallback((experience, filters) => {
    const isHotel = experience?.type === 'hotel';
    const productId = isHotel ? experience?.hotelId : experience?.id;
    pushEvent(isHotel ? GTM_EVENTS.HOTEL_ADDED : GTM_EVENTS.EXPERIENCE_ADDED, {
      [isHotel ? 'hotel_id' : 'experience_id']: productId,
      context: 'marketplace',
      assistant_session_id: assistantSessionFor(
        isHotel ? 'hotel' : 'experience',
        productId,
      ),
    });
    setBasket(basket => [
      ...basket,
      { ...experience, filters, internalId: uuidv4() },
    ]);
  }, []);
  const removeExperienceFromBasket = useCallback(experience => {
    setBasket(basket =>
      basket.filter(
        basketExp => basketExp.internalId !== experience.internalId,
      ),
    );
  }, []);
  const updateExperienceInBasket = useCallback(experience => {
    setBasket(basket =>
      basket.map(basketExp =>
        basketExp.internalId === experience.internalId ? experience : basketExp,
      ),
    );
  }, []);
  const setDates = useCallback(({ startAt, endAt }) => {
    setExperienceFilters(current => ({
      ...current,
      startAt,
      endAt,
    }));
  }, []);
  const resetBasket = useCallback(() => {
    setBasket([]);
  }, []);

  const value = {
    experienceFilters,
    setExperienceFilters,
    resetContext,
    basket,
    addExperienceToBasket,
    removeExperienceFromBasket,
    updateExperienceInBasket,
    setDates,
    resetBasket,
    setBasket,
    catalogPage,
    setCatalogPage,
    catalogPerPage,
    setCatalogPerPage,
  };
  return (
    <ExperienceBookingContext.Provider value={value}>
      {children}
    </ExperienceBookingContext.Provider>
  );
};

export default ExperienceBookingProvider;
